import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Input, Spinner } from '@components/ui';
import { clientService } from '@services/clientService';
import api from '@services/api';
import PortalAccessCredentials from './PortalAccessCredentials';

export default function ClientModal({
  isOpen,
  onClose,
  onSubmit,
  client = null,
  isLoading = false,
  onClientUpdated,
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    logo: '',
  });
  const [hasAccess, setHasAccess] = useState(false);
  // Link and PIN of the access just generated: the only time they can be shown
  const [freshAccess, setFreshAccess] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [logoType, setLogoType] = useState('url'); // 'url' or 'file'
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        company: client.company || '',
        logo: client.logo || '',
      });
      setHasAccess(Boolean(client.hasPortalAccess));
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        logo: '',
      });
      setHasAccess(false);
    }
    setFreshAccess(null);
  }, [client, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      email: formData.email || null,
      phone: formData.phone || null,
      company: formData.company || null,
      logo: formData.logo || null,
    });
  };

  const handleGenerateToken = async () => {
    if (!client?.id) return;
    if (
      hasAccess &&
      !window.confirm('¿Regenerar acceso? El enlace y el PIN actuales dejarán de funcionar.')
    ) {
      return;
    }
    setTokenLoading(true);
    try {
      const response = await clientService.generateToken(client.id);
      setFreshAccess({ accessToken: response.data.accessToken, accessPin: response.data.accessPin });
      setHasAccess(true);
      onClientUpdated?.();
    } catch (error) {
      console.error('Error generating token:', error);
    } finally {
      setTokenLoading(false);
    }
  };

  const handleRevokeToken = async () => {
    if (!client?.id) return;
    setTokenLoading(true);
    try {
      await clientService.revokeToken(client.id);
      setFreshAccess(null);
      setHasAccess(false);
      onClientUpdated?.();
    } catch (error) {
      console.error('Error revoking token:', error);
    } finally {
      setTokenLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten archivos de imagen');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo no debe superar los 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setFormData((prev) => ({ ...prev, logo: response.data.data.url }));
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error al subir la imagen');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setFormData((prev) => ({ ...prev, logo: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={client ? 'Editar Cliente' : 'Nuevo Cliente'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isLoading}>
            {client ? 'Guardar' : 'Crear Cliente'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <Input
          label="Nombre del Cliente"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Nombre del cliente"
          required
        />

        <Input
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="email@ejemplo.com"
        />

        <Input
          label="Teléfono"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="+1 234 567 890"
        />

        <Input
          label="Empresa"
          name="company"
          value={formData.company}
          onChange={handleChange}
          placeholder="Nombre de la empresa"
        />

        {/* Logo field with tabs for URL or file upload */}
        <div>
          <label
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px',
            }}
          >
            Logo del Cliente
          </label>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button
              type="button"
              onClick={() => setLogoType('file')}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '500',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: logoType === 'file' ? '#dbeafe' : '#f3f4f6',
                color: logoType === 'file' ? '#1d4ed8' : '#6b7280',
              }}
            >
              Subir imagen
            </button>
            <button
              type="button"
              onClick={() => setLogoType('url')}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '500',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: logoType === 'url' ? '#dbeafe' : '#f3f4f6',
                color: logoType === 'url' ? '#1d4ed8' : '#6b7280',
              }}
            >
              URL externa
            </button>
          </div>

          {logoType === 'file' ? (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  cursor: uploadingLogo ? 'wait' : 'pointer',
                  backgroundColor: uploadingLogo ? '#f9fafb' : 'transparent',
                  transition: 'all 0.2s',
                }}
              >
                {uploadingLogo ? (
                  <>
                    <Spinner size="sm" />
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Subiendo...</span>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '24px' }}>📁</span>
                    <span style={{ fontSize: '14px', color: '#4b5563' }}>
                      Clic para seleccionar imagen
                    </span>
                  </>
                )}
              </label>
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                Formatos: JPG, PNG, GIF, WebP. Tamaño máximo: 5MB
              </p>
            </div>
          ) : (
            <Input
              name="logo"
              value={formData.logo}
              onChange={handleChange}
              placeholder="https://ejemplo.com/logo.png"
            />
          )}

          {/* Logo preview */}
          {formData.logo && (
            <div
              style={{
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
              }}
            >
              <img
                src={formData.logo}
                alt="Logo preview"
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '1px solid #e5e7eb',
                }}
                onError={(e) => {
                  e.target.src = '';
                  e.target.alt = 'Error';
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Vista previa</p>
                <p
                  style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formData.logo}
                </p>
              </div>
              <button
                type="button"
                onClick={handleRemoveLogo}
                style={{
                  padding: '6px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
                title="Eliminar logo"
              >
                🗑️
              </button>
            </div>
          )}
        </div>

        {client && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Acceso al Portal del Cliente
            </label>
            {hasAccess ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <span className="text-green-600 dark:text-green-400 flex-shrink-0">🔑</span>
                  <span className="text-sm text-green-700 dark:text-green-300 truncate flex-1">
                    Acceso activo
                  </span>
                </div>

                {freshAccess ? (
                  <PortalAccessCredentials
                    accessToken={freshAccess.accessToken}
                    accessPin={freshAccess.accessPin}
                  />
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    El enlace y el PIN solo se muestran al generarlos. Si el cliente los perdió, regenera el acceso.
                  </p>
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleGenerateToken}
                    loading={tokenLoading}
                    className="flex-1"
                  >
                    🔄 Regenerar acceso
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={handleRevokeToken}
                    loading={tokenLoading}
                  >
                    🗑️ Revocar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <span className="text-gray-400 flex-shrink-0">🔒</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Sin acceso al portal
                  </span>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleGenerateToken}
                  loading={tokenLoading}
                  className="w-full"
                >
                  🔑 Generar acceso al portal
                </Button>
              </div>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}

ClientModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  client: PropTypes.object,
  isLoading: PropTypes.bool,
  onClientUpdated: PropTypes.func,
};
